import React, { useMemo } from "react"

import { useAgentsById } from "@/agents/catalog"
import DiffMatchPatch from "diff-match-patch"

import { ExpandableTemplateVariableText } from "@/components/shared/ExpandableTemplateVariableText"

import { useFindReplaceContext } from "../../../contexts/FindReplaceContext"
import { useTestSetEditorContext } from "../../../contexts/TestSetEditorContext"
import { EVENTS, useEventDispatcher } from "../../../event-bus"
import type { EditableTestCase } from "../../../model"
import { getHighlightState } from "../../../utils/findReplaceHighlight"
import { getPendingEditForTestCase } from "../../../utils/pendingChangesValidation"
import { DraggableListEditor } from "./DraggableListEditor"
import { useGridCell } from "./useGridCell"

interface TestCaseBehaviorsCellProps {
  testCase: EditableTestCase
  isExpanded: boolean
  width: string
  isMarkedForDeletion?: boolean
}

export const TestCaseBehaviorsCell: React.FC<TestCaseBehaviorsCellProps> = ({
  testCase,
  isExpanded,
  width,
  isMarkedForDeletion = false,
}) => {
  const dispatch = useEventDispatcher()
  const {
    pendingChanges,
    activeAgentId,
    expectedBehaviorTestCaseId,
    requestExpectedBehaviorTest,
  } = useTestSetEditorContext()
  const { findReplaceState } = useFindReplaceContext()
  const { agentsById } = useAgentsById()
  const pendingEdit = getPendingEditForTestCase(testCase.id, pendingChanges)

  const { isEditing, cellProps, tdRef } = useGridCell({
    ariaLabel: "Edit expected behaviors",
  })

  const behaviors = useMemo(
    () => testCase.expected_behaviors || [],
    [testCase.expected_behaviors],
  )

  const activeAgent = useMemo(() => {
    if (activeAgentId && agentsById[activeAgentId]) {
      return agentsById[activeAgentId]
    }
    return null
  }, [activeAgentId, agentsById])

  const entityAttributes = useMemo(
    () => ({
      test_case: Object.keys(testCase.expected_output_json || {}),
      agent: activeAgent ? Object.keys(activeAgent.attributes || {}) : [],
    }),
    [testCase.expected_output_json, activeAgent],
  )

  const entityValues = useMemo(
    () => ({
      agent: activeAgent?.attributes || {},
      test_case: testCase.expected_output_json || {},
    }),
    [activeAgent, testCase.expected_output_json],
  )

  const handleUpdateBehaviors = (behaviors: string[]) => {
    dispatch(EVENTS.TEST_CASE_UPDATE, {
      testCaseId: testCase.id,
      updates: { expected_behaviors: behaviors },
    })
  }

  const handleDeleteBehavior = (index: number) => {
    const updatedBehaviors = behaviors.filter((_, i) => i !== index)
    handleUpdateBehaviors(updatedBehaviors)
    requestAnimationFrame(() => tdRef.current?.focus())
  }

  const pendingBehaviors = pendingEdit?.expected_behaviors

  type WordDiff = [number, string] // [-1|0|1, text]
  type DiffItem =
    | { status: "unchanged" | "added" | "removed"; text: string }
    | { status: "modified"; words: WordDiff[] }

  const diffBehaviors = useMemo((): DiffItem[] | null => {
    if (!pendingBehaviors) {
      return null
    }

    const dmp = new DiffMatchPatch()
    const { chars1, chars2, lineArray } = dmp.diff_linesToChars_(
      behaviors.map((b) => `${b}\n`).join(""),
      pendingBehaviors.map((b) => `${b}\n`).join(""),
    )
    const lineDiffs = dmp.diff_main(chars1, chars2, false)
    dmp.diff_charsToLines_(lineDiffs, lineArray)

    return lineDiffs
      .flatMap(([op, text]) =>
        text
          .split("\n")
          .filter(Boolean)
          .map<{ status: "unchanged" | "added" | "removed"; text: string }>(
            (line) => ({
              text: line,
              status: op === -1 ? "removed" : op === 1 ? "added" : "unchanged",
            }),
          ),
      )
      .reduce<DiffItem[]>((acc, item) => {
        const prev = acc[acc.length - 1]
        if (prev?.status === "removed" && item.status === "added") {
          const wordDiffs = dmp.diff_main(prev.text, item.text)
          dmp.diff_cleanupSemantic(wordDiffs)
          acc[acc.length - 1] = { status: "modified", words: wordDiffs }
        } else {
          acc.push(item)
        }
        return acc
      }, [])
  }, [behaviors, pendingBehaviors])

  const renderContent = () => {
    // Editing with a pending diff: render the diff read-only (non-draggable).
    if (isEditing && diffBehaviors) {
      return (
        <div className="flex flex-col gap-2 w-full min-w-0">
          {diffBehaviors.map((item, index) => (
            <div
              key={`behavior-diff-${index}`}
              className="flex gap-1 items-start"
            >
              <div className="flex-1 text-sm whitespace-pre-wrap outline-none bg-surface-1 rounded p-2 min-h-8 text-coval-text">
                {item.status === "modified" ? (
                  item.words.map(([op, text], wi) => (
                    <span
                      key={wi}
                      className={
                        op === 1
                          ? "text-coval-success-text"
                          : op === -1
                            ? "line-through text-coval-danger-text opacity-60"
                            : ""
                      }
                    >
                      {text}
                    </span>
                  ))
                ) : (
                  <span
                    className={
                      item.status === "removed"
                        ? "line-through text-coval-danger-text opacity-60"
                        : item.status === "added"
                          ? "text-coval-success-text"
                          : ""
                    }
                  >
                    {item.text}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )
    }

    // Editing without a diff: shared draggable list editor. Behaviors focuses a
    // wrapper <div> (ExpandableTemplateVariableText manages its own focus) and
    // handles Enter/Backspace at the cell level, so onKeyDown is unused here and
    // a freshly-added empty row is committed immediately (commitOnAdd).
    if (isEditing) {
      return (
        <DraggableListEditor<HTMLDivElement>
          items={behaviors}
          onItemsChange={handleUpdateBehaviors}
          addLabel="behavior"
          deleteLabel="behavior"
          commitOnAdd
          selectOnFocus={false}
          renderItem={({ value, index, onChange, registerRef }) => {
            const highlightState = getHighlightState(
              findReplaceState,
              testCase.id,
              "expected_behaviors",
              index.toString(),
            )
            return (
              <div ref={registerRef} tabIndex={-1}>
                <ExpandableTemplateVariableText
                  value={value}
                  onChange={(newValue) => {
                    // Mirror prior behaviour: empty value deletes the row.
                    if (newValue.trim() === "") {
                      handleDeleteBehavior(index)
                    } else {
                      onChange(newValue)
                    }
                  }}
                  isExpanded
                  focusOnMount
                  placeholder="The agent should ..."
                  entityAttributes={entityAttributes}
                  entityValues={entityValues}
                  className="text-sm text-coval-text"
                  highlightText={highlightState.highlightText}
                  isHighlighted={highlightState.isCurrentMatch}
                />
              </div>
            )
          }}
        />
      )
    }

    // Compute diff items when a pending edit exists
    const renderBehaviorItems = (maxItems?: number) => {
      if (diffBehaviors) {
        const visible = maxItems
          ? diffBehaviors.slice(0, maxItems)
          : diffBehaviors
        const remaining = maxItems
          ? Math.max(0, diffBehaviors.length - maxItems)
          : 0
        if (diffBehaviors.length === 0) {
          return (
            <span className="text-sm text-coval-text-muted select-none">
              The agent should ...
            </span>
          )
        }
        return (
          <>
            <ul className="list-disc list-outside pl-5 space-y-3 w-full min-w-0">
              {visible.map((item, i) => (
                <li
                  key={i}
                  className="text-sm leading-tight whitespace-pre-wrap wrap-break-word text-coval-text"
                >
                  {item.status === "modified" ? (
                    item.words.map(([op, text], wi) => (
                      <span
                        key={wi}
                        className={
                          op === 1
                            ? "text-coval-success-text"
                            : op === -1
                              ? "line-through text-coval-danger-text opacity-60"
                              : ""
                        }
                      >
                        {text}
                      </span>
                    ))
                  ) : (
                    <span
                      className={
                        item.status === "removed"
                          ? "line-through text-coval-danger-text opacity-60"
                          : item.status === "added"
                            ? "text-coval-success-text"
                            : ""
                      }
                    >
                      {item.text}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            {remaining > 0 && (
              <div className="text-xs text-coval-text-muted mt-3">
                +{remaining} more
              </div>
            )}
          </>
        )
      }

      const displayList = maxItems ? behaviors.slice(0, maxItems) : behaviors
      const remaining = maxItems ? Math.max(0, behaviors.length - maxItems) : 0
      if (behaviors.length === 0) {
        return (
          <span className="text-sm text-coval-text-muted select-none">
            The agent should ...
          </span>
        )
      }
      return (
        <>
          <ul className="list-disc list-outside pl-5 space-y-3 w-full min-w-0">
            {displayList.map((behavior, index) => {
              const highlightState = getHighlightState(
                findReplaceState,
                testCase.id,
                "expected_behaviors",
                index.toString(),
              )
              return (
                <li
                  key={`behavior-${index}`}
                  className={`text-sm leading-tight whitespace-pre-wrap wrap-break-word text-coval-text ${isMarkedForDeletion ? "line-through opacity-50" : ""}`}
                >
                  <ExpandableTemplateVariableText
                    value={behavior}
                    isExpanded={false}
                    placeholder="The agent should ..."
                    entityAttributes={entityAttributes}
                    entityValues={entityValues}
                    className="text-sm text-coval-text"
                    highlightText={highlightState.highlightText}
                    isHighlighted={highlightState.isCurrentMatch}
                    maxCollapsedLength={maxItems ? 200 : Infinity}
                  />
                </li>
              )
            })}
          </ul>
          {remaining > 0 && (
            <div className="text-xs text-coval-text-muted mt-3">
              +{remaining} more
            </div>
          )}
        </>
      )
    }

    // Expanded (Expand All): show all behaviors read-only
    if (isExpanded) {
      return renderBehaviorItems()
    }

    // Collapsed: show up to 3 behaviors + chip
    return renderBehaviorItems(3)
  }

  return (
    <td
      {...cellProps}
      onClickCapture={() => {
        if (
          expectedBehaviorTestCaseId &&
          expectedBehaviorTestCaseId !== testCase.id &&
          !testCase.id.startsWith("new-")
        ) {
          requestExpectedBehaviorTest(testCase.id)
        }
      }}
      className="p-0 outline-none"
      style={{ width, padding: 0, verticalAlign: "top" }}
    >
      <div className="relative px-2 pb-4 pt-4">{renderContent()}</div>
    </td>
  )
}
